import { Component, OnInit, Output, EventEmitter, Input, HostBinding } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { HasSubscriptions } from '../../has-subscriptions';
import { CommunicationService } from '../../../services/communication.service';
import { ListingCategory, SCREEN_SIZE } from '../../../classes/enums';
import { Img, Header } from '../../../classes/interfaces';
import { StaticPathPipe } from '../../../shared/pipes/static-path.pipe';
import { Router } from '@angular/router';

const commercialImgHeight = 30;
const residentialImgHeight = 23;
const luxuryActiveImgHeight = 62;
const luxuryInactiveImgHeight = 30;

const commercialImgWidth = 140;
const residentialImgWidth = 140;
const luxuryActiveImgWidth = 200;
const luxuryInactiveImgWidth = 140;

@Component({
  selector: 'app-left-nav',
  templateUrl: './left-nav.component.html',
  styleUrls: ['./left-nav.component.scss'],
  animations: [
    trigger('leftNavTrigger', [
      transition(':enter', [
        style({ left: '-100vw' }),
        animate('350ms ease-out', style({ left: '0' }))
      ]),
      transition(':leave', [
        style({ left: '*' }),
        animate('350ms ease-in', style({ left: '-100vw' }))
      ])
    ])
  ]
})
export class LeftNavComponent extends HasSubscriptions implements OnInit {
  @HostBinding('class.active')
  @Input() showLeftNav: boolean = false;
  @Output() onListingsClick: EventEmitter<void> = new EventEmitter();
  @Output() onCloseLeftNavClick: EventEmitter<void> = new EventEmitter();
  platform: ListingCategory = ListingCategory.RESIDENTIAL;
  screenSize: SCREEN_SIZE;

  icons: Header = {
    top: {
      className: '',
      img: { src: '', height: 0, width: 0 },
      link: ''
    },
    left: {
      className: '',
      img: { src: '', height: 0, width: 0 },
      link: ''
    },
    right: {
      className: '',
      img: { src: '', height: 0, width: 0 },
      link: ''
    }
  };

  imgs: { [key: number]: { n: string; w: string } } = {
    [ListingCategory.RESIDENTIAL]: { n: 'REMAX_masterLogo@1x.svg', w: 'REMAX_masterLogo_white@1x.svg' },
    [ListingCategory.LUXURY]: { n: 'REMAX_Collection.svg', w: 'REMAX_Collection_white.svg' },
    [ListingCategory.COMMERCIAL]: { n: 'REMAX_Commercial.svg', w: 'REMAX_Commercial_white.svg' }
  };
  // enums
  ListingPropertyType = ListingCategory;

  constructor(private _communicationService: CommunicationService,
    private staticPathPipe: StaticPathPipe,
    private router: Router) {
    super();
    this.addSub(this._communicationService.listings.currentShownListingType.subscribe((s) => this.platform = s));
    this.addSub(this._communicationService.listings.currentShownListingType.subscribe((listingType) => this.setState(listingType)));

  }
  ngOnInit() { }

  async changeListingType(newListingType: ListingCategory, e: MouseEvent) {
    e.preventDefault();
    this._communicationService.setCurrentListingType(newListingType);
    await this.router.navigate([newListingType === ListingCategory.RESIDENTIAL ? '/' : '/' + ListingCategory[newListingType].toLowerCase()]);
  }

  setState(nextListingCategory: ListingCategory): void {
    if (nextListingCategory === ListingCategory.RESIDENTIAL) {
      this.icons.top.img = this.getLinkImg(ListingCategory.RESIDENTIAL, nextListingCategory);
      this.icons.right.img = this.getLinkImg(ListingCategory.COMMERCIAL, nextListingCategory);
      this.icons.right.link = this.getLinkUrl(ListingCategory.COMMERCIAL, nextListingCategory);
      this.icons.left.img = this.getLinkImg(ListingCategory.LUXURY, nextListingCategory);
      this.icons.left.link = this.getLinkUrl(ListingCategory.LUXURY, nextListingCategory);
    } else if (nextListingCategory === ListingCategory.LUXURY) {
      this.icons.top.img = this.getLinkImg(ListingCategory.LUXURY, nextListingCategory);
      this.icons.left.img = this.getLinkImg(ListingCategory.RESIDENTIAL, nextListingCategory);
      this.icons.left.link = this.getLinkUrl(ListingCategory.RESIDENTIAL, nextListingCategory);
      this.icons.right.img = this.getLinkImg(ListingCategory.COMMERCIAL, nextListingCategory);
      this.icons.right.link = this.getLinkUrl(ListingCategory.COMMERCIAL, nextListingCategory);
    } else {
      this.icons.top.img = this.getLinkImg(ListingCategory.COMMERCIAL, nextListingCategory);
      this.icons.right.img = this.getLinkImg(ListingCategory.RESIDENTIAL, nextListingCategory);
      this.icons.right.link = this.getLinkUrl(ListingCategory.RESIDENTIAL, nextListingCategory);
      this.icons.left.img = this.getLinkImg(ListingCategory.LUXURY, nextListingCategory);
      this.icons.left.link = this.getLinkUrl(ListingCategory.LUXURY, nextListingCategory);
    }
  }

  getLinkImg(listingCategory: ListingCategory, nextListingCategory: ListingCategory): Img {
    return {
      src: this.getLinkImgSrc(listingCategory, nextListingCategory),
      height: this.getLinkImgHeight(listingCategory, nextListingCategory),
      width: this.getLinkImgWidth(listingCategory, nextListingCategory),
      alt: `side-nav.left-nav.${ListingCategory[listingCategory].toLowerCase()}`,
      type: listingCategory
    }
  }

  getLinkUrl(listingCategory: ListingCategory, nextListingCategory: ListingCategory): string {
    if (listingCategory == ListingCategory.COMMERCIAL) {
      return '/commercial';
    } else if (listingCategory == ListingCategory.LUXURY) {
      return '/luxury';
    } else {
      return '/';
    }
  }

  getLinkImgSrc(listingCategory: ListingCategory, nextListingCategory: ListingCategory): string {
    const isLuxury = nextListingCategory === ListingCategory.LUXURY;
    const img = isLuxury ? this.imgs[listingCategory]['w'] : this.imgs[listingCategory]['n'];
    return this.staticPathPipe.transform(`/assets/REMAX_Logo/${img}`);
  }

  getLinkImgHeight(listingCategory: ListingCategory, nextListingCategory: ListingCategory): number {
    const isActive = listingCategory === nextListingCategory;
    switch (nextListingCategory) {
      case ListingCategory.COMMERCIAL: {
        return commercialImgHeight;
      }
      case ListingCategory.LUXURY: {
        return isActive ? luxuryActiveImgHeight : luxuryInactiveImgHeight;
      }
      default: {
        return residentialImgHeight;
      }
    }

  }

  getLinkImgWidth(listingCategory: ListingCategory, nextListingCategory: ListingCategory): number {
    const isActive = listingCategory === nextListingCategory;
    switch (nextListingCategory) {
      case ListingCategory.COMMERCIAL: {
        return commercialImgWidth;
      }
      case ListingCategory.LUXURY: {
        return isActive ? luxuryActiveImgWidth : luxuryInactiveImgWidth;
      }
      default: {
        return residentialImgWidth;
      }
    }
  }
}

